import { CreditCardTwoTone } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ICard, IParams, Page, PermissionKeys, Table, useHasPermission, Widget } from '../../../shared';
import {
    DownloadCardAction,
    DuplicatesWarning,
    LinkCardModal,
    SupportCaseTitle,
    UnlinkCardAction,
} from '../../components';
import { useGetSupportCase } from '../../hooks';

export const SupportCaseCardsPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [linkCardModal, setLinkCardModal] = useState(false);
    const { name } = window.carity.environment.theme;

    const { hasPermission } = useHasPermission();
    const hasSupportCasePermission = hasPermission(PermissionKeys.SUPPORT_CASES_WRITE);
    const hasCardsWritePermission = hasPermission(PermissionKeys.CARDS_DELETE);

    const { id } = useParams<keyof IParams>() as IParams;
    const { data: supportCase } = useGetSupportCase(id);

    const columns: GridColDef[] = [
        {
            field: 'code',
            headerName: t('code'),
            minWidth: 200,
            flex: 1,
            sortable: false,
        },
        ...(hasSupportCasePermission
            ? ([
                  {
                      field: 'actions',
                      headerName: t('actions'),
                      sortable: false,
                      minWidth: 100,
                      align: name === 'antwerp' ? 'center' : 'right',
                      renderCell: ({ row }: GridCellParams<ICard>) => {
                          return (
                              <>
                                  {supportCase && hasSupportCasePermission && hasCardsWritePermission && (
                                      <UnlinkCardAction supportCase={supportCase} card={row} />
                                  )}
                                  {supportCase && <DownloadCardAction cardId={row.id} />}
                              </>
                          );
                      },
                  },
              ] as GridColDef[])
            : ([] as GridColDef[])),
    ];

    return (
        <Page onBack={() => navigate(-1)} title={supportCase && <SupportCaseTitle supportCase={supportCase} />}>
            <Stack spacing={2}>
                <DuplicatesWarning supportCaseId={id} />
                <Widget
                    icon={<CreditCardTwoTone color="primary" />}
                    title={t('cards')}
                    hasTable
                    actions={
                        hasSupportCasePermission &&
                        supportCase && (
                            <Button variant="contained" onClick={() => setLinkCardModal(true)} data-testid="link-card">
                                {t('linkCard')}
                            </Button>
                        )
                    }
                >
                    <Table rows={supportCase?.cards || []} columns={columns} hideFooter paginationMode="client" />
                </Widget>
                {supportCase && (
                    <LinkCardModal
                        open={linkCardModal}
                        handleClose={() => setLinkCardModal(false)}
                        supportCase={supportCase.id}
                    />
                )}
            </Stack>
        </Page>
    );
};
